/* Серверный источник шаблонной «Истории»: движок рисует те же список, лист и просмотрщики, данные и файлы берёт отсюда. */
import { endpoints } from '../../api';
import { ApiError } from '../../api/client';
import type { HistoryAsset, HistoryOrder, HistoryQuery, HistoryStats, HistoryTab } from '../../api/history';
import { uiText } from '../../i18n';
import { clearDeliveryIntent, deliveryIntent } from './history-delivery-intent';
import { activeProgress, deliveryFormats, financialDisplay, isActive, needsOrderSync, orderTitle } from './history-model';
import { resultLines, textBlocks, videoRows, type ResultLine, type TextBlock, type VideoRow } from './history-result-helpers';

/** Работа в виде, который понимает движок: вид студии, статус строки и исходный заказ для действий */
export interface HistoryItem {
  id: number;
  kind: string;
  title: string;
  at: number;
  status: 'done' | 'fail' | 'mod' | 'wait' | 'run';
  fav: number;
  price: number | null;
  dur: number;
  text: string;
  vid: number | null;
  voiceName?: string;
  characterName?: string;
  /** Имя шаблона заказа («Шаблон» в сведениях листа) */
  app?: string;
  /** Свой аватар заказа «Свой аватар» (result.avatar_id): имя и голос — из своих аватаров */
  av?: number;
  voiceCount?: number;
  /** Имена голосов ролей на момент заказа (по репликам), по порядку появления */
  voiceNames?: string[];
  finance: ReturnType<typeof financialDisplay>;
  progress: ReturnType<typeof activeProgress>;
  ar?: string;
  style?: string;
  n?: number;
  /** «Повтори движения»: кто повторил (me · ph · person · char); нет — сервер героя не назвал */
  who?: string;
  order: HistoryOrder;
}

export interface HistoryMedia {
  url: string;
  dur: number;
}

/** Файлы результата по типу; blob-адреса живут до unmount страницы */
export interface HistoryResult {
  unavailable: boolean;
  images: string[];
  videos: HistoryMedia[];
  audios: HistoryMedia[];
  texts: TextBlock[];
  lines?: ResultLine[];
  looks?: Array<{ n: string; indices: number[] }>;
  /** Ряды роликов (группы video_set; без групп — все ролики одним рядом) */
  rows?: VideoRow[];
  archive: boolean;
  /** Пропорции «ширина:высота» первого кадра или ролика, если сервер их знает */
  ar?: string;
}

export type SendOutcome = 'sent' | 'accepted' | 'uncertain';

const TABS: HistoryTab[] = ['all', 'fav', 'circle', 'video', 'photo', 'shoot', 'audio', 'text'];
/** Экраны сервера, у которых в студии другое имя вида */
const KIND: Record<string, string> = {
  who: 'circle',
  voice_s2s: 'voice',
  clean: 'voice',
  transcription: 'text',
  slides: 'infog',
  audio: 'tts',
};
const TEXT_LIMIT = 512 * 1024;
const THUMB_PARALLEL = 3;

const kindOf = (order: HistoryOrder) => KIND[order.screen] ?? (order.screen || KIND[order.kind] || order.kind);
const displayText = (order: HistoryOrder, key: string) => {
  const value = order.display?.[key];
  return typeof value === 'string' && value ? value : undefined;
};

/** Герой «Повтори движения» по входам повтора: сервер хранит cast или разовое photo (motion-n8n-v1), а не who экрана; у пресета «Стать персонажем» строки нет */
function danceWho(order: HistoryOrder): string | undefined {
  const inputs = order.repeat?.inputs ?? {};
  if (inputs.preset === 'swap') return undefined;
  if (typeof inputs.photo === 'string' && inputs.photo) return 'ph';
  const cast = typeof inputs.cast === 'string' ? inputs.cast : '';
  // свой аватар — тоже «Вы», как в итоге формы (motionDoneText)
  if (cast === 'self' || cast.startsWith('user_avatar:')) return 'me';
  if (cast.startsWith('person:')) return 'person';
  if (cast.startsWith('character:')) return 'char';
  return typeof inputs.who === 'string' && inputs.who ? inputs.who : undefined;
}

/** Текст поста: только у экрана post (подпись партнёрского рилса или доставки — не текст поста) */
function postCaption(order: HistoryOrder): string | undefined {
  const caption = (order.result as { caption?: unknown } | null | undefined)?.caption;
  return order.screen === 'post' && typeof caption === 'string' && caption ? caption : undefined;
}

function statusOf(order: HistoryOrder): HistoryItem['status'] {
  if (order.status === 'done') return 'done';
  if (isActive(order)) return 'run';
  if (order.status === 'uncertain') return 'wait';
  return order.error_code?.includes('moderation') ? 'mod' : 'fail';
}

function priceOf(order: HistoryOrder) {
  if (['not_required', 'dev_free'].includes(order.financial?.state ?? '')) return 0;
  const raw = order.financial?.charged_amount ?? order.charge?.debited ?? order.financial?.amount_tokens ?? order.price;
  const amount = raw == null ? null : Number(raw);
  return amount != null && Number.isFinite(amount) ? Math.abs(amount) : null;
}

export function toItem(order: HistoryOrder): HistoryItem {
  const images = (order.result?.assets ?? []).filter(asset => asset.mime.startsWith('image/')).length;
  const kind = kindOf(order),
    text = order.text || order.result?.text || '';
  return {
    id: order.id,
    kind,
    // у озвучки без названия строкой служит сам текст, как в шаблоне
    title: order.title || (kind === 'tts' && text ? '' : orderTitle(order)),
    at: Date.parse(order.created_at),
    status: statusOf(order),
    fav: order.fav ? 1 : 0,
    price: priceOf(order),
    dur: (order.result?.duration_ms ?? 0) / 1000,
    text,
    vid: order.voice_id ?? null,
    voiceName: displayText(order, 'voice_name'),
    characterName: displayText(order, 'who') ?? displayText(order, 'character_name'),
    voiceCount:
      new Set(
        resultLines({ timeline: order.result?.timeline, source_lines: order.result?.source_lines })
          .map(line => line.voiceId)
          .filter(id => id != null),
      ).size || undefined,
    voiceNames: [
      ...new Set(
        resultLines({ timeline: order.result?.timeline, source_lines: order.result?.source_lines })
          .map(line => line.voiceName)
          .filter((name): name is string => !!name),
      ),
    ],
    finance: financialDisplay(order),
    progress: activeProgress(order),
    ar: displayText(order, 'aspect_ratio') ?? displayText(order, 'ar'),
    style: displayText(order, 'style'),
    app: (typeof order.app === 'string' ? order.app : order.app?.name)?.trim() || undefined,
    av: order.result?.avatar_id,
    n: images || undefined,
    who: kind === 'dance' ? danceWho(order) : undefined,
    order,
  };
}

/** Файлы результата: явные assets, иначе одиночный download_url; у старой озвучки — /result/audio */
function assetsOf(order: HistoryOrder): HistoryAsset[] {
  const result = order.result;
  if (result?.assets?.length) return result.assets;
  if (result?.download_url && !result.archive_url)
    return [{ index: 0, mime: result.mime || (order.kind === 'tts' ? 'audio/mpeg' : 'video/mp4'), download_url: result.download_url }];
  if (order.kind === 'tts' && order.status === 'done')
    return [{ index: 0, mime: 'audio/mpeg', download_url: `/app/orders/${order.id}/result/audio` }];
  return [];
}

const pathOf = (order: HistoryOrder, asset: HistoryAsset) =>
  asset.download_url || `/app/orders/${order.id}/result/files/${asset.index}?revision=${order.result_revision ?? 1}`;

/* blob-адреса: один запрос на файл, повтор после ошибки — новым обращением */
const urls = new Map<string, Promise<string>>();
function blobUrl(order: HistoryOrder, asset: HistoryAsset): Promise<string> {
  const key = `${order.id}:${order.result_revision ?? 1}:${pathOf(order, asset)}`;
  let url = urls.get(key);
  if (!url) {
    url = endpoints.history.download(order.id, pathOf(order, asset)).then(blob => URL.createObjectURL(blob));
    url.catch(() => urls.delete(key));
    urls.set(key, url);
  }
  return url;
}

/* миниатюры строк: не больше трёх загрузок разом, чтобы страница истории не забивала канал */
const thumbs = new Map<number, string | null>();
const queue: Array<() => void> = [];
let running = 0;
function limited<T>(job: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const run = () => {
      running++;
      job()
        .then(resolve, reject)
        .finally(() => {
          running--;
          queue.shift()?.();
        });
    };
    if (running < THUMB_PARALLEL) run();
    else queue.push(run);
  });
}

const thumbAsset = (order: HistoryOrder) =>
  order.result?.previews?.find(asset => asset.mime.startsWith('image/')) ??
  assetsOf(order).find(asset => asset.mime.startsWith('image/')) ??
  null;

async function textOf(order: HistoryOrder, asset: HistoryAsset) {
  const blob = await endpoints.history.download(order.id, pathOf(order, asset));
  if (blob.size > TEXT_LIMIT) throw new Error('result_text_too_large');
  const raw = await blob.text();
  if (asset.mime !== 'application/json') return { texts: [{ label: '', text: raw }], lines: [] };
  const data: unknown = JSON.parse(raw),
    lines = resultLines(data);
  return { texts: lines.length ? [] : textBlocks(data), lines };
}

const media = (order: HistoryOrder, asset: HistoryAsset) =>
  blobUrl(order, asset).then(url => ({ url, dur: (asset.duration_ms ?? order.result?.duration_ms ?? 0) / 1000 }));

export function historyError(error: unknown): string {
  if (error instanceof Error && error.message === 'delivery_intent_storage_unavailable')
    return uiText('Не удалось сохранить отправку. Разрешите хранилище вкладки и попробуйте ещё раз.');
  if (error instanceof ApiError) {
    if (error.status === 401) return uiText('Сессия истекла. Откройте приложение заново.');
    if (error.status === 403) return uiText('Эта работа недоступна вашему аккаунту.');
    if (error.status === 404) return uiText('Работа больше не найдена. Обновите историю.');
    if ([409, 412].includes(error.status)) return uiText('Работа обновилась. Загрузите её ещё раз.');
    if (error.status === 429) return uiText('Слишком много запросов. Подождите немного.');
    if (error.code === 'result_expired') return uiText('Результат больше недоступен');
    if (error.status === 422) return uiText('Не удалось выполнить действие с этой работой. Обновите её и попробуйте снова.');
  }
  return uiText('Не удалось загрузить историю. Попробуйте ещё раз.');
}

const REJECTED = ['delivery_request_invalid', 'order_not_found', 'result_unavailable', 'result_expired', 'delivery_format_unavailable'];

export const historyServer = {
  async page(tab: string, q: string, sort: string, cursor: string | null) {
    const query: HistoryQuery = {
      tab: TABS.includes(tab as HistoryTab) ? (tab as HistoryTab) : 'all',
      q: q.trim(),
      sort: sort === 'old' ? 'old' : 'new',
    };
    const page = await endpoints.history.list(query, cursor);
    return { items: page.items.map(toItem), cursor: page.next_cursor };
  },
  stats: (): Promise<HistoryStats> => endpoints.history.stats(),
  async active(): Promise<HistoryItem[]> {
    const page = await endpoints.history.active();
    return page.items.filter(order => isActive(order) || order.status === 'uncertain').map(toItem);
  },
  get: async (id: number) => toItem(await endpoints.history.get(id)),
  seen: (id: number) => endpoints.history.seen(id).catch(() => {}),
  favorite: async (id: number, fav: boolean) => (await endpoints.history.favorite(id, fav)).fav,
  hide: (id: number) => endpoints.history.hide(id),
  /** Миниатюра: blob-адрес превью, null — у работы нет картинки (значок вида) */
  thumbNow: (order: HistoryOrder) => thumbs.get(order.id),
  async thumb(order: HistoryOrder): Promise<string | null> {
    if (thumbs.has(order.id)) return thumbs.get(order.id) ?? null;
    const asset = thumbAsset(order);
    let url = asset ? await limited(() => blobUrl(order, asset)).catch(() => null) : null;
    /* картинки в итоге нет, а ролик есть (кружок, видео): кадр из ролика (GET …/result/files/{i}/poster), иначе строка стояла со значком раздела */
    const video = !url && order.status === 'done' ? assetsOf(order).find(a => a.mime.startsWith('video/')) : undefined;
    if (video)
      url = await limited(() =>
        endpoints.history
          .download(order.id, `/app/orders/${order.id}/result/files/${video.index}/poster?revision=${order.result_revision ?? 1}`)
          .then(blob => (blob.type.startsWith('image/') ? URL.createObjectURL(blob) : null)),
      ).catch(() => null);
    thumbs.set(order.id, url);
    return url;
  },
  async result(order: HistoryOrder): Promise<HistoryResult> {
    const result = order.result;
    if (result?.available === false) return { unavailable: true, images: [], videos: [], audios: [], texts: [], archive: false };
    const assets = assetsOf(order);
    const pick = (prefix: string) => assets.filter(asset => asset.mime.startsWith(prefix));
    const textAssets = assets.filter(asset => asset.mime === 'application/json' || asset.mime.startsWith('text/'));
    const [images, videos, audios, fileTexts] = await Promise.all([
      Promise.all(pick('image/').map(asset => blobUrl(order, asset))),
      Promise.all(pick('video/').map(asset => media(order, asset))),
      Promise.all(pick('audio/').map(asset => media(order, asset))),
      Promise.all(textAssets.map(asset => textOf(order, asset))),
    ]);
    const body = order.text || result?.text,
      caption = postCaption(order);
    const inlineLines = resultLines({ timeline: result?.timeline, source_lines: result?.source_lines });
    const lines = inlineLines.length ? inlineLines : [...resultLines(result?.data), ...fileTexts.flatMap(file => file.lines)];
    // тексты слайдов уже на картинках слайдов: отдельными блоками их не показываем (как в шаблоне — ряд слайдов и текст поста)
    const texts = [
      ...(body ? [{ label: uiText('Текст'), text: body }] : []),
      ...fileTexts.flatMap(file => file.texts),
      ...(lines.length ? [] : textBlocks(result?.data)),
      ...(caption ? [{ label: uiText('Текст поста'), text: caption }] : []),
    ];
    const sized = assets.find(asset => asset.width && asset.height && /^(image|video)\//.test(asset.mime));
    return {
      unavailable: false,
      images,
      videos,
      audios,
      texts,
      lines,
      looks: result?.sections
        ?.map(section => ({
          n: section.title ?? section.name ?? section.kind,
          indices: section.asset_indices.map(index => pick('image/').findIndex(asset => asset.index === index)).filter(index => index >= 0),
        }))
        .filter(section => section.indices.length),
      rows: videoRows(result?.data, pick('video/').map(asset => asset.index)),
      archive: !!result?.archive_url && assets.length > 1,
      ar: sized ? `${sized.width}:${sized.height}` : undefined,
    };
  },
  /** Картинка i результата файлом для «Скачать» лайтбокса */
  async image(order: HistoryOrder, i: number) {
    const asset = assetsOf(order).filter(item => item.mime.startsWith('image/'))[i];
    if (!asset) return null;
    const data = await endpoints.history.download(order.id, pathOf(order, asset));
    return {
      data,
      name: asset.file_name || `steos-${order.screen}-${i + 1}.${(asset.mime.split('/')[1] || 'jpg').replace('jpeg', 'jpg')}`,
    };
  },
  async archive(order: HistoryOrder) {
    const url = order.result?.archive_url;
    if (!url) return null;
    return { data: await endpoints.history.download(order.id, url), name: `steos-${order.screen}.zip` };
  },
  canSend: (order: HistoryOrder) => order.status === 'done' && deliveryFormats(order).length > 0,
  needsSync: needsOrderSync,
  /** Отправка в чат: ключ хранится до ответа, неизвестный исход повторяется с тем же ключом */
  async send(order: HistoryOrder): Promise<SendOutcome> {
    if (order.status !== 'done' || order.result?.available === false || !deliveryFormats(order).length)
      throw new ApiError(422, 'result_unavailable', '');
    const format = deliveryFormats(order)[0];
    // Владелец — из свежего /me, а не из номера Telegram или старого prefill.
    const owner = await endpoints.me();
    const intent = deliveryIntent(owner.user.id, order.id, order.result_revision ?? 1, format);
    try {
      const state = (await endpoints.history.send(order, format, intent.key)).delivery?.state;
      if (state === 'sent' || state === 'delivered') {
        clearDeliveryIntent(intent);
        return 'sent';
      }
      // Пустой или неизвестный ответ не подтверждает постановку в очередь.
      return ['pending', 'queued', 'accepted', 'sending'].includes(state ?? '') ? 'accepted' : 'uncertain';
    } catch (error) {
      if (error instanceof ApiError && REJECTED.includes(error.code)) clearDeliveryIntent(intent);
      throw error;
    }
  },
  /** «Повторить» с настройками заказа: событие принимает bridge/history-repeat; false — повторять нечего */
  repeat(order: HistoryOrder) {
    if (!order.repeat) return false;
    window.dispatchEvent(new CustomEvent('steos:history-repeat', { detail: order.repeat }));
    return true;
  },
  error: historyError,
  /** Снять blob-адреса, когда страница ушла */
  release() {
    urls.forEach(url => void url.then(URL.revokeObjectURL, () => {}));
    urls.clear();
    thumbs.clear();
  },
};

export type HistoryServer = typeof historyServer;
